/**
 * Swap Navigator Component
 *
 * Permet de naviguer dans une chaîne de swaps avec boutons prev/next
 * Affiche l'origine de l'investissement et permet de remonter l'historique
 */

import { useState, useMemo } from 'react';
import { Icons } from '../Icons';
import { fmt } from '../../utils/formatters';
import { getSwapChain } from '../../utils/costBasisTracking';
import type { Token, Position, Prices } from '../../types';

interface SwapNavigatorProps {
  currentToken: Token;
  allTokens: Token[];
  prices: Prices;
  onNavigateToToken?: (tokenId: string) => void;
}

export function SwapNavigator({
  currentToken,
  allTokens,
  prices,
  onNavigateToToken
}: SwapNavigatorProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Vérifier si le token fait partie d'une chaîne
  const hasSwapHistory = currentToken.positions.some(
    p => p.is_swap || p.swap_chain_id
  );

  // IMPORTANT: hooks must be called in the same order on every render —
  // early returns above were violating that. Compute the swap chain
  // unconditionally, then bail out below if it's empty.
  const swapChain = useMemo(() => {
    if (!hasSwapHistory) return [];
    const firstPosition = currentToken.positions[0];
    const chain = getSwapChain(firstPosition, allTokens);
    return chain.map(position => {
      const token = allTokens.find(t =>
        t.positions.some(p => p.id === position.id)
      );
      return {
        position,
        token: token || null,
        tokenSymbol: token?.displayName || token?.id || 'Unknown',
        tokenId: token?.id || '',
      };
    });
  }, [hasSwapHistory, currentToken, allTokens]);

  if (!hasSwapHistory || swapChain.length <= 1) {
    return null;
  }

  const currentStep = swapChain[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === swapChain.length - 1;

  // Calculer les métriques du step actuel
  const currentPrice = currentStep.token
    ? prices[currentStep.tokenId]?.usd || currentStep.position.price
    : currentStep.position.price;

  const originalCostBasis = currentStep.position.original_cost_basis || currentStep.position.price;
  const currentValue = currentStep.position.quantity * currentPrice;
  const costValue = currentStep.position.quantity * originalCostBasis;
  const performance = costValue > 0 ? ((currentValue - costValue) / costValue) * 100 : 0;

  return (
    <div style={{
      marginTop: '16px',
      padding: '16px',
      background: 'var(--bg-secondary)',
      borderRadius: '8px',
      border: '1px solid var(--border-color)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icons.ArrowRightLeft size={16} style={{ color: 'var(--accent)' }} />
          <span style={{ fontSize: '14px', fontWeight: '600' }}>
            Navigation Swap Chain
          </span>
        </div>
        <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
          {currentIndex + 1} / {swapChain.length}
        </div>
      </div>

      {/* Current Step Card */}
      <div style={{
        padding: '16px',
        background: 'var(--bg)',
        borderRadius: '8px',
        border: isLast ? '2px solid var(--accent)' : '1px solid var(--border-color)',
        marginBottom: '12px',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          gap: '16px',
        }}>
          {/* Left: Token info */}
          <div style={{ flex: 1 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px',
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: isLast ? 'var(--accent)' : 'var(--bg-secondary)',
                border: '2px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '600',
                color: isLast ? 'white' : 'var(--text)',
              }}>
                {currentStep.tokenSymbol.substring(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>
                  {currentStep.tokenSymbol}
                </div>
                {isFirst && (
                  <div style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    background: 'rgba(34, 197, 94, 0.1)',
                    color: '#22c55e',
                    display: 'inline-block',
                    marginTop: '2px',
                  }}>
                    ORIGINE
                  </div>
                )}
                {isLast && (
                  <div style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    background: 'var(--accent)',
                    color: 'white',
                    display: 'inline-block',
                    marginTop: '2px',
                  }}>
                    ACTUEL
                  </div>
                )}
              </div>
            </div>

            <div style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '4px' }}>
              {fmt(currentStep.position.quantity, 8)} @ ${fmt(currentStep.position.price, 2)}
            </div>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              ${fmt(currentStep.position.quantity * currentStep.position.price, 0)}
            </div>

            {/* Date */}
            <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
              {new Date(currentStep.position.date).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
            </div>

            {/* Note si swap */}
            {currentStep.position.note && currentStep.position.is_swap && (
              <div style={{
                marginTop: '8px',
                padding: '6px 8px',
                background: 'rgba(99, 102, 241, 0.1)',
                borderRadius: '4px',
                fontSize: '11px',
                color: 'var(--muted)',
                fontStyle: 'italic',
              }}>
                {currentStep.position.note}
              </div>
            )}
          </div>

          {/* Right: Performance */}
          <div style={{
            padding: '12px',
            borderRadius: '6px',
            background: performance >= 0 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            textAlign: 'right',
          }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '4px' }}>
              {isLast ? 'Maintenant' : 'Au swap'}
            </div>
            <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '2px' }}>
              ${fmt(currentPrice, 2)}
            </div>
            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              color: performance >= 0 ? '#22c55e' : '#ef4444',
            }}>
              {performance >= 0 ? '+' : ''}{fmt(performance, 1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
      }}>
        <button
          onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
          disabled={isFirst}
          style={{
            flex: 1,
            padding: '8px 12px',
            background: isFirst ? 'var(--bg-secondary)' : 'var(--accent)',
            color: isFirst ? 'var(--muted)' : 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isFirst ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            opacity: isFirst ? 0.5 : 1,
          }}
        >
          <Icons.ChevronLeft size={14} />
          Précédent
        </button>

        {/* Progress indicator */}
        <div style={{
          flex: 2,
          display: 'flex',
          gap: '4px',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {swapChain.map((_, index) => (
            <div
              key={index}
              onClick={() => setCurrentIndex(index)}
              style={{
                width: index === currentIndex ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                background: index === currentIndex
                  ? 'var(--accent)'
                  : index < currentIndex
                    ? 'rgba(99, 102, 241, 0.3)'
                    : 'var(--border-color)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            />
          ))}
        </div>

        <button
          onClick={() => setCurrentIndex(Math.min(swapChain.length - 1, currentIndex + 1))}
          disabled={isLast}
          style={{
            flex: 1,
            padding: '8px 12px',
            background: isLast ? 'var(--bg-secondary)' : 'var(--accent)',
            color: isLast ? 'var(--muted)' : 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isLast ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            opacity: isLast ? 0.5 : 1,
          }}
        >
          Suivant
          <Icons.ChevronRight size={14} />
        </button>
      </div>

      {/* Summary footer */}
      <div style={{
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid var(--border-color)',
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '11px',
        color: 'var(--muted)',
      }}>
        <div>
          {isFirst ? (
            <>💰 Investissement initial</>
          ) : (
            <>🔄 Swap {currentIndex} / {swapChain.length - 1}</>
          )}
        </div>
        <div>
          Chain ID: {currentStep.position.swap_chain_id?.substring(0, 12)}...
        </div>
      </div>
    </div>
  );
}
